import './PageMenu.css';

import React from 'react';
import PropTypes from 'prop-types';
import config from '@config';
import sociosIcon from '../../assets/icons/socios-icon.svg';
import whiteStarIcon from '../../assets/icons/white-star.svg';

import noop from 'lodash/noop';

import { Switch } from 'antd';
import Button from '@views/Button';

export const SwitchForecastResults = ({ onChange, selected }) => {
  return (
    <div className="SwitchForecastResults">
      <span className={selected === 'forecast' ? 'selected' : ''}>
        Pronóstico
      </span>
      <Switch
        onChange={(checked) => onChange(checked ? 'results' : 'forecast')}
        checked={selected === 'results'}
      />
      <span className={selected === 'results' ? 'selected' : ''}>
        Resultados
      </span>
    </div>
  );
};

SwitchForecastResults.displayName = 'SwitchForecastResults';
SwitchForecastResults.propTypes = {
  selected: PropTypes.oneOf(['results', 'forecast']),
  onChange: PropTypes.func,
};
SwitchForecastResults.defaultProps = {
  selected: 'results',
  onChange: noop,
};

export const PageMenu = ({
  selected,
  onChange,
  onAction,
  onActivateWildcard,
  canChangeForecast,
  isSaving,
  enableSave,
  isContributor,
  isNewMonthlyMember,
  userEmail,
  wildcardsAvailable,
  canActivateWildcardNow,
  shareMode,
}) => {
  const [showWildcardPopup, setShowWildcardPopup] = React.useState(false);
  // Los socios mensuales nuevos no acceden a la funcionalidad de cambios:
  // se les oculta el botón de comodines y se les ofrece el plan anual.
  const hasChangesAccess = isContributor && !isNewMonthlyMember;
  const showWildcardButton = !shareMode && hasChangesAccess;
  const showBecomeMemberButton = !shareMode && !hasChangesAccess;
  const publicBasePath = process.env.PUBLIC_URL || config.basePath || '';

  const buildAssetUrl = React.useCallback(
    (assetPath) => {
      if (typeof window === 'undefined') return `${publicBasePath}${assetPath}`;
      const normalizedBase = publicBasePath
        ? `${String(publicBasePath).replace(/\/$/, '')}/`
        : '/';
      const normalizedAssetPath = String(assetPath).replace(/^\//, '');
      return new URL(`${normalizedBase}${normalizedAssetPath}`, window.location.origin).toString();
    },
    [publicBasePath]
  );

  const buildSociosUrl = React.useCallback(() => {
    if (!config.sociosURL) return null;
    const returnUrl =
      typeof window !== 'undefined' ? window.location.pathname : '';
    try {
      const url = new URL(config.sociosURL);
      if (config.clientId) url.searchParams.set('source', config.clientId);
      if (userEmail) url.searchParams.set('partner_email', userEmail);
      if (returnUrl) url.searchParams.set('return_url', returnUrl);
      return url.toString();
    } catch (error) {
      const separator = config.sociosURL.includes('?') ? '&' : '?';
      const sourceParam = config.clientId
        ? `source=${encodeURIComponent(config.clientId)}`
        : '';
      const emailParam = userEmail
        ? `partner_email=${encodeURIComponent(userEmail)}`
        : '';
      const returnUrlParam = returnUrl
        ? `return_url=${encodeURIComponent(returnUrl)}`
        : '';
      const query = [sourceParam, emailParam, returnUrlParam]
        .filter(Boolean)
        .join('&');
      return query ? `${config.sociosURL}${separator}${query}` : config.sociosURL;
    }
  }, [userEmail]);

  const handleBecomeMemberClick = React.useCallback(() => {
    const sociosLink = buildSociosUrl();
    if (!sociosLink) return;
    window.location.assign(sociosLink);
  }, [buildSociosUrl]);

  return (
    <div className="PageMenu">
      <div className="page-menu__controls">
        <div className="page-menu__switch-col">
          <SwitchForecastResults onChange={onChange} selected={selected} />
          <p className="page-menu__switch-help">
            {selected === 'forecast'
              ? 'Estás viendo los resultados que pronosticaste.'
              : 'Estás viendo los resultados reales de los partidos del Mundial.'}
          </p>
        </div>
        {!shareMode && canChangeForecast && (
          <div className="page-menu__button-col">
            <Button
              type="secondary"
              className="page-menu__save-btn"
              disabled={!enableSave}
              onClick={onAction}
              loading={isSaving}
            >
              Guardar pronóstico
            </Button>
          </div>
        )}
        {showWildcardButton && (
          <div className="page-menu__wildcard-col">
            <Button
              type="secondary"
              className="page-menu__wildcard-btn"
              disabled={!canActivateWildcardNow}
              onClick={() => setShowWildcardPopup(true)}
            >
              {wildcardsAvailable === 1
                ? 'Te queda 1 cambio disponible'
                : `Te quedan ${wildcardsAvailable} cambios disponibles`}
            </Button>
          </div>
        )}
        {/* Pasate al plan ANUAL para hacer cambios */}
        {showBecomeMemberButton && (
          <div className="page-menu__wildcard-col">
            <Button
              type="danger"
              className="page-menu__become-member-btn"
              onClick={handleBecomeMemberClick}
            >
              <span className="page-menu__become-member-content">
               {!isNewMonthlyMember ? <><span className="page-menu__become-member-text">Hacete</span>
                <img
                  src={sociosIcon}
                  alt="Socios"
                  className="page-menu__become-member-socios-icon"
                />
                <span className="page-menu__become-member-text">
                  para hacer cambios
                </span>
                <img
                  src={whiteStarIcon}
                  alt=""
                  aria-hidden="true"
                  className="page-menu__become-member-star-icon"
                /></>: (
                <>
                  <span className="page-menu__become-member-text">
                    Pasate al plan ANUAL para hacer cambios
                  </span>
                  <img
                    src={whiteStarIcon}
                    alt=""
                    aria-hidden="true"
                    className="page-menu__become-member-star-icon"
                  />
                </>)}
              </span>
            </Button>
          </div>
        )}
      </div>
      {showWildcardPopup && (
        <div
          className="page-menu__wildcard-modal-backdrop"
          onClick={() => setShowWildcardPopup(false)}
        >
          <div
            className="page-menu__wildcard-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <p>
              Vas a reemplazar tus pronósticos de esta fase por nuevos
              cruces basados en los equipos realmente clasificados.
            </p>
            <ul>
              <li>El cambio no suma ni resta puntos</li>
              <li>Solo puede usarse antes del inicio de la fase.</li>
              <li>No podrás hacer cambios en la final.</li>
            </ul>
            <p><strong>¿Deseás continuar?</strong></p>
            <div className="page-menu__wildcard-actions">
              <Button
                type="secondary"
                className="page-menu__wildcard-activate-btn"
                disabled={!canActivateWildcardNow}
                onClick={() => {
                  onActivateWildcard();
                  setShowWildcardPopup(false);
                }}
              >
                <span className="page-menu__wildcard-activate-content">
                  Realizar cambios
                  <img
                    src={buildAssetUrl('/icons/star.png')}
                    alt=""
                    aria-hidden="true"
                    className="page-menu__wildcard-star"
                  />
                </span>
              </Button>
              <button
                type="button"
                className="page-menu__wildcard-cancel"
                onClick={() => setShowWildcardPopup(false)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

PageMenu.displayName = 'PageMenu';
PageMenu.propTypes = {
  selected: PropTypes.oneOf(['results', 'forecast']),
  canChangeForecast: PropTypes.bool,
  onChange: PropTypes.func,
  onAction: PropTypes.func,
  onActivateWildcard: PropTypes.func,
  isSaving: PropTypes.bool,
  enableSave: PropTypes.bool,
  isContributor: PropTypes.bool,
  isNewMonthlyMember: PropTypes.bool,
  userEmail: PropTypes.string,
  wildcardsAvailable: PropTypes.number,
  canActivateWildcardNow: PropTypes.bool,
  shareMode: PropTypes.bool,
};
PageMenu.defaultProps = {
  selected: 'results',
  canChangeForecast: false,
  onChange: noop,
  onAction: noop,
  onActivateWildcard: noop,
  isSaving: false,
  enableSave: false,
  isContributor: false,
  isNewMonthlyMember: false,
  userEmail: '',
  wildcardsAvailable: 0,
  canActivateWildcardNow: false,
  shareMode: false,
};

export default PageMenu;
